import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Radio,
    RadioGroup,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    alpha,
    useTheme,
} from '@mui/material';
import { styled } from '@mui/system';
import { debounce } from 'lodash';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BugReportIcon from '@mui/icons-material/BugReport';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import {
    getFp1DebugSessions,
    getFp1DebugDownload,
    getFp1DebugSuggestions,
    startFp1DebugSessionExport,
    getFp1DebugSessionExport,
    type Fp1DebugSuggestion,
} from '../fp1.api';
import type { Fp1DebugSession, Fp1DebugSessionExport, Fp1DebugUpload } from '../fp1.types';

// ── Styled ────────────────────────────────────────────────────────────────────

const FilterBar = styled('form')(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(3),
}));

// ── Helpers ───────────────────────────────────────────────────────────────────

// The session endpoint reads uploads via the raw query builder, so MySQL decimal
// columns arrive as strings (not floats). Coerce defensively before formatting.
const fmt = (v: number | string | null | undefined, unit = '') => {
    if (v == null || v === '') return '--';
    const n = typeof v === 'number' ? v : parseFloat(v);
    return Number.isFinite(n) ? `${n.toFixed(2)}${unit}` : '--';
};

// `summary_max_velocity` is stored/returned in m/s (see backend
// PerformanceDataUploadResource + gps-session-analysis lambda). Convert to mph
// for display — the rest of the app (ForceVelocityProfile, UploadsController
// exports) applies the same 2.23694 factor. Without this the raw m/s value was
// being shown mislabelled as "mph" (e.g. 10.01 m/s displayed as "10.01 mph"
// instead of 22.39 mph).
const MPS_TO_MPH = 2.23694;
const fmtMph = (v: number | string | null | undefined) => {
    if (v == null || v === '') return '--';
    const n = typeof v === 'number' ? v : parseFloat(v);
    return Number.isFinite(n) ? `${(n * MPS_TO_MPH).toFixed(2)} mph` : '--';
};

const fmtDate = (d: string | null) =>
    d
        ? new Date(d).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
          })
        : '--';

// start_date_time comes back as the stored wall-clock string ("2024-08-06 11:00:00").
// Format it as-is rather than through Date, which would shift it by the browser's offset.
const fmtStartTime = (dt: string | null) => {
    const m = dt?.match(/[ T](\d{2}):(\d{2})/);
    if (!m) return null;
    const h = parseInt(m[1], 10);
    return `${h % 12 || 12}:${m[2]} ${h < 12 ? 'AM' : 'PM'}`;
};

// ── Whole-session download ───────────────────────────────────────────────────

const EXPORT_POLL_MS = 2000;
const EXPORT_MAX_WAIT_MS = 5 * 60 * 1000;

const SessionDownloadButton: React.FC<{ session: Fp1DebugSession }> = ({ session }) => {
    const [job, setJob] = useState<Fp1DebugSessionExport | null>(null);
    const [error, setError] = useState('');
    const timer = useRef<ReturnType<typeof setTimeout>>();
    const cancelled = useRef(false);

    useEffect(() => {
        cancelled.current = false;
        return () => {
            cancelled.current = true;
            clearTimeout(timer.current);
        };
    }, []);

    const busy = job?.status === 'queued' || job?.status === 'running';

    const poll = (exportId: string, startedAt: number) => {
        timer.current = setTimeout(async () => {
            if (cancelled.current) return;
            try {
                const res = await getFp1DebugSessionExport(exportId);
                const next = res.data?.data;
                if (cancelled.current || !next) return;
                setJob(next);
                if (next.status === 'ready' && next.download_url) {
                    window.open(next.download_url, '_blank', 'noopener,noreferrer');
                } else if (next.status === 'failed') {
                    setError(next.error || 'Export failed. Try again.');
                } else if (Date.now() - startedAt > EXPORT_MAX_WAIT_MS) {
                    setError('Export is taking too long. Try again in a few minutes.');
                    setJob(null);
                } else {
                    poll(exportId, startedAt);
                }
            } catch {
                if (!cancelled.current) {
                    setError('Lost track of the export. Try again.');
                    setJob(null);
                }
            }
        }, EXPORT_POLL_MS);
    };

    const handleClick = async (e: React.MouseEvent) => {
        // The button sits in the clickable card header — don't toggle the card.
        e.stopPropagation();
        setError('');
        try {
            const res = await startFp1DebugSessionExport(session.uploads.map((u) => u.uuid));
            const started = res.data?.data;
            if (!started) throw new Error('No export returned');
            setJob(started);
            if (started.status === 'ready' && started.download_url) {
                window.open(started.download_url, '_blank', 'noopener,noreferrer');
            } else if (started.status === 'failed') {
                setError(started.error || 'Export failed. Try again.');
            } else {
                poll(started.export_id, Date.now());
            }
        } catch (err: any) {
            setError(err?.response?.data?.error ?? 'Failed to start the export. Try again.');
        }
    };

    // A finished export's signed link expires after 30 min — ask for a fresh one,
    // and rebuild only if the export itself has expired.
    const handleRedownload = async (e: React.MouseEvent) => {
        e.stopPropagation();
        try {
            const res = await getFp1DebugSessionExport(job!.export_id);
            const url = res.data?.data?.download_url;
            if (url) {
                window.open(url, '_blank', 'noopener,noreferrer');
                return;
            }
        } catch {
            // fall through to a fresh export
        }
        handleClick(e);
    };

    const canRedownload = job?.status === 'ready' && !!job.download_url && !error;

    const label = busy
        ? job?.stage || 'Preparing…'
        : job?.status === 'ready'
        ? 'Download again'
        : 'Download session';

    return (
        <Tooltip
            title={
                error ||
                'Zip of every athlete/pod in this session: README, anomalies, per-pod summary, ' +
                    '10s overview and a merged per-second timeline — built for spotting bugs across pods ' +
                    '(and easy to hand to an AI).'
            }
        >
            {/* data-no-toggle: the card header ignores clicks in here (incl. on the disabled button) */}
            <span data-no-toggle>
                <Button
                    size="small"
                    variant="outlined"
                    color={error ? 'error' : 'primary'}
                    startIcon={
                        busy ? <CircularProgress size={12} /> : <FolderZipIcon fontSize="small" />
                    }
                    disabled={busy}
                    onClick={canRedownload ? handleRedownload : handleClick}
                    sx={{
                        textTransform: 'none',
                        fontSize: '0.75rem',
                        whiteSpace: 'nowrap',
                        maxWidth: 220,
                    }}
                >
                    <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {error ? 'Retry download' : label}
                    </Box>
                </Button>
            </span>
        </Tooltip>
    );
};

// ── Upload row ────────────────────────────────────────────────────────────────

const UploadRow: React.FC<{ upload: Fp1DebugUpload }> = ({ upload }) => {
    const theme = useTheme();
    const [downloading, setDownloading] = useState(false);
    const [downloadErr, setDownloadErr] = useState('');

    const handleDownload = async () => {
        setDownloading(true);
        setDownloadErr('');
        try {
            const res = await getFp1DebugDownload(upload.uuid);
            const url = res.data?.data?.download_url;
            if (!url) throw new Error('No download URL returned');
            window.open(url, '_blank', 'noopener,noreferrer');
        } catch {
            setDownloadErr('Failed to get download link. Try again.');
        } finally {
            setDownloading(false);
        }
    };

    const hasError = !!upload.error;
    const isComplete = !!upload.completed_at;

    return (
        <>
            <TableRow
                sx={{
                    bgcolor: hasError ? alpha(theme.palette.error.main, 0.06) : 'transparent',
                    '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                    },
                }}
            >
                {/* Status */}
                <TableCell sx={{ width: 40, pr: 0 }}>
                    <Tooltip title={hasError ? upload.error! : isComplete ? 'Complete' : 'Pending'}>
                        {hasError ? (
                            <ErrorOutlineIcon color="error" fontSize="small" />
                        ) : (
                            <CheckCircleOutlineIcon color="success" fontSize="small" />
                        )}
                    </Tooltip>
                </TableCell>

                {/* Athlete */}
                <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
                        {upload.athlete_name || '--'}
                    </Typography>
                </TableCell>

                {/* Pod SN */}
                <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                        <Typography
                            variant="body2"
                            sx={{ fontFamily: 'monospace', fontWeight: 600 }}
                        >
                            {upload.pod_serial_number || '--'}
                        </Typography>
                        {upload.pod_serial_number && (
                            <Tooltip title="Copy SN">
                                <IconButton
                                    size="small"
                                    sx={{ p: '2px', opacity: 0.5, '&:hover': { opacity: 1 } }}
                                    onClick={() =>
                                        navigator.clipboard.writeText(upload.pod_serial_number!)
                                    }
                                >
                                    <ContentCopyIcon sx={{ fontSize: 13 }} />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                    {upload.pod_fw_version && (
                        <Typography variant="caption" color="text.secondary">
                            FW {upload.pod_fw_version}
                        </Typography>
                    )}
                </TableCell>

                {/* Data type */}
                <TableCell>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem' }}>
                        {upload.sprint_data_type || '--'}
                    </Typography>
                </TableCell>

                {/* Max velocity */}
                <TableCell align="right">
                    <Typography variant="body2">{fmtMph(upload.summary_max_velocity)}</Typography>
                </TableCell>

                {/* Max accel */}
                <TableCell align="right">
                    <Typography variant="body2">
                        {fmt(upload.summary_max_acceleration, ' m/s²')}
                    </Typography>
                </TableCell>

                {/* Accel events */}
                <TableCell align="right">
                    <Typography variant="body2">
                        {upload.summary_number_of_accelerations ?? '--'}
                    </Typography>
                </TableCell>

                {/* Error */}
                <TableCell sx={{ maxWidth: 200 }}>
                    {hasError ? (
                        <Typography
                            variant="caption"
                            color="error"
                            sx={{ display: 'block', wordBreak: 'break-word' }}
                        >
                            {upload.error}
                        </Typography>
                    ) : (
                        <Typography variant="caption" color="text.secondary">
                            —
                        </Typography>
                    )}
                </TableCell>

                {/* Download */}
                <TableCell align="right">
                    {upload.has_download ? (
                        <Tooltip title="Download full raw data CSV (30-min link)">
                            <span>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={
                                        downloading ? (
                                            <CircularProgress size={12} />
                                        ) : (
                                            <FileDownloadIcon fontSize="small" />
                                        )
                                    }
                                    disabled={downloading}
                                    onClick={handleDownload}
                                    sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                                >
                                    {downloading ? 'Loading…' : 'Download'}
                                </Button>
                            </span>
                        </Tooltip>
                    ) : (
                        <Typography variant="caption" color="text.secondary">
                            No file
                        </Typography>
                    )}
                </TableCell>
            </TableRow>
            {downloadErr && (
                <TableRow>
                    <TableCell colSpan={9} sx={{ py: 0.5 }}>
                        <Alert severity="error" sx={{ py: 0 }}>
                            {downloadErr}
                        </Alert>
                    </TableCell>
                </TableRow>
            )}
        </>
    );
};

// ── Session card ──────────────────────────────────────────────────────────────

const SessionCard: React.FC<{ session: Fp1DebugSession }> = ({ session }) => {
    const theme = useTheme();
    const [open, setOpen] = useState(false);
    const errorCount = session.uploads.filter((u) => u.error).length;

    return (
        <Paper
            variant="outlined"
            sx={{
                borderRadius: 2,
                overflow: 'hidden',
                mb: 1.5,
                borderColor: errorCount > 0 ? alpha(theme.palette.error.main, 0.3) : 'divider',
            }}
        >
            {/* Header row */}
            <Box
                onClick={(e) => {
                    if ((e.target as HTMLElement).closest('[data-no-toggle]')) return;
                    setOpen((v) => !v);
                }}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    px: 2,
                    py: 1.5,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.03) },
                }}
            >
                <IconButton size="small" sx={{ p: 0.25 }}>
                    {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>

                {/* Date + start time (two sessions on one day are separate cards) */}
                <Box sx={{ minWidth: 110 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                        {fmtDate(session.date)}
                    </Typography>
                    {fmtStartTime(session.start_date_time) && (
                        <Typography variant="caption" color="text.secondary">
                            {fmtStartTime(session.start_date_time)}
                        </Typography>
                    )}
                </Box>

                {/* Org name + session title */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        variant="body2"
                        noWrap
                        sx={{ color: 'text.secondary' }}
                        title={session.organization_name || undefined}
                    >
                        {session.organization_name ||
                            (session.organization_uuid
                                ? `Org ${session.organization_uuid.slice(0, 8)}`
                                : 'No org')}
                    </Typography>
                    {session.title && (
                        <Typography variant="caption" color="text.secondary" noWrap component="div">
                            {session.title}
                        </Typography>
                    )}
                </Box>

                {/* Counts */}
                <Box display="flex" gap={1} flexShrink={0}>
                    <Chip
                        label={`${session.athlete_count} athlete${
                            session.athlete_count !== 1 ? 's' : ''
                        }`}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.72rem' }}
                    />
                    <Chip
                        label={`${session.pod_count} pod${session.pod_count !== 1 ? 's' : ''}`}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.72rem' }}
                    />
                    <Chip
                        label={`${session.upload_count} upload${
                            session.upload_count !== 1 ? 's' : ''
                        }`}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.72rem' }}
                    />
                    {errorCount > 0 && (
                        <Chip
                            label={`${errorCount} error${errorCount !== 1 ? 's' : ''}`}
                            size="small"
                            color="error"
                            sx={{ fontSize: '0.72rem' }}
                        />
                    )}
                </Box>

                <SessionDownloadButton session={session} />
            </Box>

            {/* Expanded upload list — mounted only when open (a single in-house org can
                return hundreds of sessions; mounting every hidden table would be costly) */}
            <Collapse in={open} mountOnEnter unmountOnExit>
                <Box sx={{ borderTop: '1px solid', borderColor: 'divider', overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'action.hover' }}>
                                <TableCell sx={{ width: 40 }} />
                                <TableCell>Athlete</TableCell>
                                <TableCell>Pod SN</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell align="right">Max Velocity</TableCell>
                                <TableCell align="right">Max Accel</TableCell>
                                <TableCell align="right">Accel Events</TableCell>
                                <TableCell>Error</TableCell>
                                <TableCell align="right">File</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {session.uploads.map((u) => (
                                <UploadRow key={u.uuid} upload={u} />
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            </Collapse>
        </Paper>
    );
};

// ── Main tab ──────────────────────────────────────────────────────────────────

const Fp1DebuggingTab: React.FC = () => {
    const [searchType, setSearchType] = useState<'org' | 'athlete' | 'serial'>('org');
    const [searchValue, setSearchValue] = useState('');
    const [sessions, setSessions] = useState<Fp1DebugSession[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [searched, setSearched] = useState(false);

    // Quick-filter the loaded sessions by a secondary term
    const [secondaryFilter, setSecondaryFilter] = useState('');
    const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');

    // ── Typeahead suggestions ────────────────────────────────────────────────
    const [suggestions, setSuggestions] = useState<Fp1DebugSuggestion[]>([]);
    const [suggestLoading, setSuggestLoading] = useState(false);
    const [inputValue, setInputValue] = useState('');

    // Debounced lookup. Bound to the current searchType via the dependency array.
    const fetchSuggestions = useMemo(
        () =>
            debounce((type: 'org' | 'athlete' | 'serial', q: string) => {
                if (q.trim().length < 2) {
                    setSuggestions([]);
                    setSuggestLoading(false);
                    return;
                }
                setSuggestLoading(true);
                getFp1DebugSuggestions(type, q.trim())
                    .then((res) => setSuggestions(res.data?.data ?? []))
                    .catch(() => setSuggestions([]))
                    .finally(() => setSuggestLoading(false));
            }, 250),
        [],
    );

    const handleSearch = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!searchValue.trim()) return;
        setLoading(true);
        setError('');
        setSessions([]);
        setSearched(false);
        setSecondaryFilter('');
        try {
            const term = searchValue.trim();
            const params =
                searchType === 'org'
                    ? { org_name: term }
                    : searchType === 'athlete'
                    ? { athlete_name: term }
                    : { serial_number: term };
            const res = await getFp1DebugSessions(params);
            setSessions(res.data?.data ?? []);
            setSearched(true);
        } catch (err: any) {
            setError(err?.response?.data?.error ?? 'Failed to load sessions. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const displayed = sessions
        .filter((s) => {
            if (!secondaryFilter.trim()) return true;
            const term = secondaryFilter.toLowerCase();
            return (
                s.organization_name?.toLowerCase().includes(term) ||
                s.date?.includes(term) ||
                s.athletes.some((a) => a.name.toLowerCase().includes(term)) ||
                s.uploads.some((u) => u.pod_serial_number?.toLowerCase().includes(term))
            );
        })
        .sort((a, b) => {
            const da = `${a.date ?? ''} ${a.start_date_time ?? ''}`;
            const db = `${b.date ?? ''} ${b.start_date_time ?? ''}`;
            return sortOrder === 'desc' ? db.localeCompare(da) : da.localeCompare(db);
        });

    const totalErrors = displayed.reduce(
        (acc, s) => acc + s.uploads.filter((u) => u.error).length,
        0,
    );

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                <BugReportIcon color="action" />
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                        Session Debugging
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Search by organization, athlete, or pod serial number to pull session
                        uploads and raw file downloads.
                    </Typography>
                </Box>
            </Box>

            {/* Search form */}
            <FilterBar onSubmit={handleSearch}>
                <RadioGroup
                    row
                    value={searchType}
                    onChange={(e) => {
                        setSearchType(e.target.value as 'org' | 'athlete' | 'serial');
                        // Reset the box when switching scope so stale suggestions don't linger.
                        setSearchValue('');
                        setInputValue('');
                        setSuggestions([]);
                    }}
                    sx={{ alignSelf: 'center' }}
                >
                    <FormControlLabel value="org" control={<Radio size="small" />} label="Org" />
                    <FormControlLabel
                        value="athlete"
                        control={<Radio size="small" />}
                        label="Athlete"
                    />
                    <FormControlLabel
                        value="serial"
                        control={<Radio size="small" />}
                        label="Serial #"
                    />
                </RadioGroup>

                <Autocomplete
                    freeSolo
                    options={suggestions}
                    loading={suggestLoading}
                    // The endpoint already filters server-side; don't re-filter client-side.
                    filterOptions={(opts) => opts}
                    getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.label)}
                    isOptionEqualToValue={(opt, val) =>
                        (typeof opt === 'string' ? opt : opt.value) ===
                        (typeof val === 'string' ? val : val.value)
                    }
                    inputValue={inputValue}
                    onInputChange={(_, val, reason) => {
                        setInputValue(val);
                        if (reason === 'input') {
                            // Free text is itself a valid search term (LIKE match).
                            setSearchValue(val);
                            fetchSuggestions(searchType, val);
                        }
                        if (reason === 'clear') {
                            setSearchValue('');
                            setSuggestions([]);
                        }
                    }}
                    onChange={(_, val) => {
                        if (val == null) {
                            setSearchValue('');
                        } else if (typeof val === 'string') {
                            setSearchValue(val);
                            setInputValue(val);
                        } else {
                            setSearchValue(val.value);
                            setInputValue(val.label);
                        }
                    }}
                    sx={{ minWidth: 320 }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            size="small"
                            autoFocus
                            label={
                                searchType === 'org'
                                    ? 'Organization name'
                                    : searchType === 'athlete'
                                    ? 'Athlete name'
                                    : 'Pod serial number'
                            }
                            placeholder={
                                searchType === 'org'
                                    ? 'e.g. AMT'
                                    : searchType === 'athlete'
                                    ? 'e.g. John Smith'
                                    : 'e.g. FP1-00123'
                            }
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {suggestLoading ? (
                                            <CircularProgress color="inherit" size={16} />
                                        ) : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                />

                <Button
                    type="submit"
                    variant="contained"
                    startIcon={
                        loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />
                    }
                    disabled={loading || !searchValue.trim()}
                >
                    {loading ? 'Searching…' : 'Search'}
                </Button>
            </FilterBar>

            {/* Error */}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Results */}
            {searched && !loading && (
                <>
                    {sessions.length === 0 ? (
                        <Alert severity="info">
                            No sessions found for &ldquo;{searchValue}&rdquo;. Try a different name.
                        </Alert>
                    ) : (
                        <>
                            {/* Results summary + secondary filter */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    mb: 2,
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Typography variant="body2" color="text.secondary">
                                    {displayed.length} session{displayed.length !== 1 ? 's' : ''}
                                    {totalErrors > 0 && (
                                        <span style={{ color: 'inherit' }}>
                                            {' '}
                                            &mdash;{' '}
                                            <span style={{ color: '#d32f2f', fontWeight: 600 }}>
                                                {totalErrors} upload error
                                                {totalErrors !== 1 ? 's' : ''}
                                            </span>
                                        </span>
                                    )}
                                    {sessions.length >= 25 && (
                                        <span> — showing the most recent 25</span>
                                    )}
                                </Typography>
                                <TextField
                                    size="small"
                                    placeholder="Filter by date, pod SN, athlete…"
                                    value={secondaryFilter}
                                    onChange={(e) => setSecondaryFilter(e.target.value)}
                                    sx={{ minWidth: 260 }}
                                />
                                <FormControl size="small" sx={{ minWidth: 130 }}>
                                    <InputLabel>Sort</InputLabel>
                                    <Select
                                        value={sortOrder}
                                        onChange={(e) =>
                                            setSortOrder(e.target.value as 'desc' | 'asc')
                                        }
                                        label="Sort"
                                    >
                                        <MenuItem value="desc">Newest first</MenuItem>
                                        <MenuItem value="asc">Oldest first</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>

                            {/* Session cards */}
                            {displayed.map((session) => (
                                <SessionCard key={session.session_key} session={session} />
                            ))}

                            {displayed.length === 0 && secondaryFilter && (
                                <Alert severity="info">No sessions match the filter.</Alert>
                            )}
                        </>
                    )}
                </>
            )}
        </Box>
    );
};

export default Fp1DebuggingTab;
